import { useState } from 'react'
import { HiExclamationTriangle } from 'react-icons/hi2'
import { Button } from '../../../components'
import { BaseModal } from '../../../components/Modal/BaseModal'
import type { User } from '../../../models'

interface DeleteUserModalProps {
  isOpen: boolean
  user: User | null
  onClose: () => void
  onConfirm: (userId: number) => Promise<boolean>
  isDeleting: boolean
}

export const DeleteUserModal = ({ isOpen, user, onClose, onConfirm, isDeleting }: DeleteUserModalProps) => {
  const [confirmed, setConfirmed] = useState(false)

  const handleConfirm = async () => {
    if (!user || !confirmed) return

    const success = await onConfirm(user.id)
    if (success) {
      setConfirmed(false)
      onClose()
    }
  }

  const handleClose = () => {
    setConfirmed(false)
    onClose()
  }

  if (!user) return null

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Desativar Usuário"
      icon={<HiExclamationTriangle className="w-6 h-6" />}
      description="Esta ação irá desativar o usuário permanentemente"
      size="lg"
      useStandardLayout={true}
      resizable={true}
      stickyFooter={true}
      footerContent={
        <div className="flex items-center justify-end gap-3 p-4">
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!confirmed || isDeleting}
            className="min-w-[120px]"
          >
            {isDeleting ? (
              <div className="flex items-center gap-2">
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Desativando...
              </div>
            ) : (
              'Desativar Usuário'
            )}
          </Button>
        </div>
      }
    >
      <div className="p-4 space-y-4">
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/50 rounded-lg p-4">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    <strong>Atenção:</strong> Esta ação irá desativar o usuário. O usuário não poderá mais acessar o sistema.
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Nome:</span>
                    <p className="text-base font-medium text-gray-900 dark:text-gray-100">{user.name}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Email:</span>
                    <p className="text-base font-medium text-gray-900 dark:text-gray-100">{user.email}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Função:</span>
                    <p className="text-base font-medium text-gray-900 dark:text-gray-100">{user.funcao_usuario}</p>
                  </div>
                </div>

        <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <input
            type="checkbox"
            id="confirm-delete"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            disabled={isDeleting}
            className="mt-1 w-4 h-4 text-red-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-red-500 focus:ring-2"
          />
          <label 
            htmlFor="confirm-delete" 
            className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none"
          >
            Eu entendo que esta ação irá desativar o usuário e ele não poderá mais acessar o sistema.
          </label>
        </div>
      </div>
    </BaseModal>
  )
}

